<script setup lang="ts">
import { useStore } from 'vuex';
import CutoutPosition from './CutoutPosition.vue';
import CutoutType from './CutoutType.vue';
import ShapeType from './ShapeType.vue';
import ValidationMessage from './UI/ValidationMessage.vue';
import {computed, ref, Ref, watch} from 'vue';
import EditableItem from './EditableItem.vue';
import MeasurementsHeightTransition from './UI/MeasurementsHeightTransition.vue';
import {getCurrentShapeInputComponent} from "@/components/shapes/ShapeHelper";
import { renderLength } from '@/functions/lengths';
import MessageContainer from "@/components/UI/MessageContainer.vue";
import { CutoutTypes } from '@/Models/CutoutTypes';

const store = useStore();

const isEditorVisible = ref<boolean>(true);
const isAddingCutout = ref<boolean>(false);
const isEditingExistingCutout = ref<boolean>(false);
const containerRef = ref<HTMLElement | null>(null);

const cutoutType = computed(() => store.getters.cutoutType);
const cutoutShape = computed(() => store.getters.cutoutShape);
const canUseExactCenter = computed(() => store.getters.canUseExactCenter);
const hasProblematicCorners = computed(() => store.getters.hasProblematicCorners);
const usesDefaultCutoutDimensions = computed(() => store.getters.usesDefaultCutoutDimensions);
const cutouts = computed(() => store.getters.cutouts);
const cutout = computed(() => store.getters.cutout);
const currentCutoutIndex = computed(() => store.getters.currentCutoutIndex);
const hasGroupedDimensionValidatorErrors = computed(() => store.getters.hasGroupedCutoutValidatorErrors);
const getGroupedDimensionValidationMessage = computed(() => store.getters.getGroupedCutoutValidationMessage);
const validationDimensionMessages = computed(() => {
	const validators = store.getters.getCutoutValidatorErrors;
	const excludedFields = ['cutoutOffset', 'cutoutOffsetLeft', 'cutoutOffsetBottom'];
	const filteredValidators = [...validators].filter((validator) => !excludedFields.includes(validator.field));

	return filteredValidators.map((validator) => {
		return {
			key: validator.field,
			id: `measurements-${validator.field}`,
			message: validator.getMessage(),
		};
	});
});

watch(cutout, async(newValue, oldValue) => {

	// Set the default cutout
	if (newValue && cutouts.value.length === 0) {
		store.commit("addCutout", newValue);
		editCutout(0);
	}

	// Update current cutout
	if(newValue != oldValue && newValue != null) {
		store.commit("updateCutoutByIndex", { index: currentCutoutIndex.value, cutout: newValue });
	}
});

// Add cutout when shape is selected
watch(cutoutShape, async(newValue, oldValue) => {
	if(newValue === '') {
		return;
	}

	if (newValue !== oldValue && isAddingCutout.value) {
		store.commit('updateCutoutIndex', store.getters.cutouts.length );
		store.commit('addCutout', store.getters.cutout );
	}

	isAddingCutout.value = false;
});

const isMaxSafeCutoutReached = computed(() => {
	const maxSafeCutoutPercentage = window.measurementSelectorVariables.pricingGroup.other_settings.max_safe_cutout_percentage;

	if ( store.getters.cutout === null || store.getters.getCutoutArea === 0 || store.getters.getShapeArea === 0 || maxSafeCutoutPercentage == 0 ) {
		return false;
	}

	return ( store.getters.getCutoutArea / store.getters.getShapeArea ) * 100 > maxSafeCutoutPercentage;
});

const currentProps = computed( () => {
	return {
		isCutout: true,
		action: "updateCutoutDimension",
		prefix: "cutout_",
		modifierAction: "updateCutoutModifier",
	};
} );

async function addCutout(): Promise<void> {
	isEditorVisible.value = true;
	isEditingExistingCutout.value = false;
	isAddingCutout.value = true;

	if (cutoutType.value === CutoutTypes.text) {

		// cutoutShape doesn't change for text, so we can update the cutout directly
		store.commit('updateCutoutIndex', cutouts.value.length );
		store.commit('addCutout', cutout.value );
	} else {

		// Start with a empty cutout, add after shape is selected
		await store.dispatch('resetCutoutShapeAndDimension', '');
	}
}

function saveCutout(): void {
	isEditorVisible.value = false;
	scrollTo(containerRef);

	if ( !isEditingExistingCutout.value ) {
		store.commit('updateCutoutIndex', -1);
		store.commit('updateUsingDefaultCutoutDimensions', true);

		return;
	}

	store.commit('updateCutoutByIndex', { index: currentCutoutIndex.value, cutout: cutout.value } );
	store.commit('updateCutoutIndex', -1);
}

function editCutout(index: number): void {
	isEditorVisible.value = true;
	isEditingExistingCutout.value = true;

	store.commit('updateCutoutIndex', index );
	store.dispatch('updateCurrentCutout');
}

function removeCutout(index: number): void {
	isEditorVisible.value = false;
	store.dispatch('removeCutout', index);
}

function scrollTo(view: Ref<HTMLElement | null>) {
	view.value?.scrollIntoView({ behavior: 'smooth' });
}

</script>

<template>
	<div class="cutouts-container" ref="containerRef">
		<measurements-height-transition appear mode="out-in">
			<CutoutType />
		</measurements-height-transition>

		<measurements-height-transition appear mode="out-in">
			<div class="group" v-if="cutouts.length > 0">
				<ul class="editable-list">
					<li
						:class='{
							"editable-list__item": true,
							"editable-list__item--active": currentCutoutIndex === index && isEditorVisible
						}'
						v-for="(cutout, index) in cutouts"
						:key="index"
					>
						<EditableItem
							:edit="{ index, title: $i18n.cutout.edit, disabled: false, onClick: editCutout, hidden: currentCutoutIndex !== index && isEditorVisible }"
							:remove="{ index, title: $i18n.cutout.remove, disabled: false, onClick: removeCutout, hidden: false }"
						>
							{{ $i18n.cutout.title }} {{ index + 1 }} -
							<strong>
								{{ $i18n.shapeNames[cutout.shape] }} |
								X - {{ renderLength(cutout.offsets[0].in_cm()) }} |
								Y - {{ renderLength(cutout.offsets[1].in_cm()) }}
							</strong>

						</EditableItem>
					</li>
				</ul>
			</div>
		</measurements-height-transition>

		<measurements-height-transition appear mode="out-in">
			<div class="group" v-if="cutoutType !== 'none' && isEditorVisible">

				<ShapeType
					v-if="cutoutType === 'shape'"
					:disabledShapes="['dxf', 'text']"
					:storeShapeGetter="'cutoutShape'"
					:storeShapeUpdater="'resetCutoutShapeAndDimension'"
					:is-cutout="true"
				/>

				<div v-if="cutoutShape !== '' && cutoutShape !== 'dxf'">
					<div class="shape-inputs">
						<component
							:is="getCurrentShapeInputComponent(cutoutShape)"
							v-bind="currentProps"
						/>
					</div>

					<message-container>
						<validation-message
							v-for="field in validationDimensionMessages"
							:key="field.key + '-validation-message'"
							:input-id="field.id"
						>
							{{ field.message }}
						</validation-message>

						<validation-message
							v-if="hasGroupedDimensionValidatorErrors"
							input-id="grouped-validation-message"
						>
							{{ getGroupedDimensionValidationMessage }}
						</validation-message>
					</message-container>
				</div>

				<CutoutPosition v-if="cutoutShape !== ''"/>

				<button
					v-if="cutoutShape"
					@click="saveCutout"
					class="measurements-selector__button-add measurements-selector__button-add--block"
					type="button"
				>
					{{ $i18n.cutout.save }}
				</button>
			</div>
		</measurements-height-transition>

		<message-container>
			<validation-message
				v-if="isMaxSafeCutoutReached"
				type="notice"
			>
				{{ $i18n.largeCutoutNotice }}
			</validation-message>

			<validation-message
				v-if="canUseExactCenter === false && usesDefaultCutoutDimensions === true"
				type="notice"
			>
				{{ $i18n.centerOfShapeNotice }}
			</validation-message>

			<validation-message
				v-if="hasProblematicCorners"
				type="notice"
			>
				{{ $i18n.problematicCornersNotice }}
			</validation-message>
		</message-container>

		<div v-if="cutoutType !== 'none' && !isEditorVisible">
			<button
				@click="addCutout"
				class="measurements-selector__button-add"
				type="button"
			>
				{{ $i18n.cutout.add }}
			</button>
		</div>
	</div>
</template>
<style scoped lang="scss">
.cutouts-container {
	display: flex;
	flex-direction: column;
	gap: 12px;
	scroll-margin-block-start: calc(var(--header-height) + 70px);
}

.group {
	background-color: $brand-blue-background;
	border-radius: 8px;
	padding: 20px;
}
</style>
